import type {
    IStageCurriculumDetailWire,
    IStageCurriculumGradeRow,
    IStageCurriculumLine,
    IStageData,
} from '../models/aggregated-lead.model'
import { extractGradeStrengthDisplayString } from './curriculum-grade-row-parse.util'

export interface TS1BoardCardForCurriculumApi {
    board?: string
    curriculum_id?: number | null
    organisation_curriculum_id?: number | null
    gradeRows?: { grade?: number | string; studentStrength?: string | number | null }[]
}

/**
 * Stage / aggregated API: curriculum_detail JSONB must not include primary_board;
 * board is represented by curriculum_id (master) on each line.
 */
export function sanitizeCurriculumDetailPayload(detail: unknown): IStageCurriculumDetailWire {
    if (!detail || typeof detail !== 'object') {
        return {}
    }
    const d = detail as Record<string, unknown>
    const gradesRaw = d['grades']
    if (!Array.isArray(gradesRaw)) {
        return {}
    }
    const grades: IStageCurriculumGradeRow[] = gradesRaw
        .map((g): IStageCurriculumGradeRow | null => {
            if (g !== null && typeof g === 'object' && 'grade' in g) {
                const row = g as Record<string, unknown>
                const grade = row['grade'] as number | string
                if (grade === undefined || grade === '') return null
                const strengthStr = extractGradeStrengthDisplayString(row)
                const n =
                    strengthStr === ''
                        ? NaN
                        : Number(String(strengthStr).replace(/,/g, '').trim() || 0)
                return {
                    grade,
                    student_strength: Number.isFinite(n) ? n : 0,
                }
            }
            if (g === undefined || g === '') return null
            return { grade: g as number | string, student_strength: 0 }
        })
        .filter((row): row is IStageCurriculumGradeRow => row !== null)

    return grades.length ? { grades } : {}
}

/** Positive integers, unique, stable order (matches Joi min(1) + unique). */
export function normalizeCurriculumDeleteIdsForApi(ids: unknown): number[] | undefined {
    if (!Array.isArray(ids)) {
        return undefined
    }
    const seen = new Set<number>()
    const out: number[] = []
    for (const x of ids) {
        const n = Number(x)
        if (!Number.isInteger(n) || n < 1) {
            continue
        }
        if (seen.has(n)) {
            continue
        }
        seen.add(n)
        out.push(n)
    }
    return out.length ? out : undefined
}

/**
 * organisation_curriculum_id values present on snapshot cards but absent from current cards
 * (e.g. user removed a board row). Backend deletes these before curriculum upsert.
 */
export function collectS1OrganisationCurriculumDeletes(
    previousCards: TS1BoardCardForCurriculumApi[],
    currentCards: TS1BoardCardForCurriculumApi[],
): number[] {
    const currentIds = new Set<number>()
    for (const card of currentCards) {
        const id = card.organisation_curriculum_id
        if (id != null && Number(id) > 0) {
            currentIds.add(Number(id))
        }
    }
    const out: number[] = []
    const seen = new Set<number>()
    for (const card of previousCards) {
        const id = card.organisation_curriculum_id
        if (id == null || Number(id) <= 0) {
            continue
        }
        const n = Number(id)
        if (currentIds.has(n) || seen.has(n)) {
            continue
        }
        seen.add(n)
        out.push(n)
    }
    return out
}

/** Strip primary_board (and any non-grades keys) from stage_data before PATCH /stage. */
export function normalizeStageDataCurriculumForApi(stageData: IStageData): IStageData {
    const next: IStageData = { ...stageData }

    if (next.curriculum_delete_ids !== undefined) {
        const normalized = normalizeCurriculumDeleteIdsForApi(next.curriculum_delete_ids)
        if (normalized?.length) {
            next.curriculum_delete_ids = normalized
        } else {
            delete next.curriculum_delete_ids
        }
    }

    if (next.curriculum_detail !== undefined) {
        const sanitized = sanitizeCurriculumDetailPayload(next.curriculum_detail)
        if (Object.keys(sanitized).length) {
            next.curriculum_detail = sanitized
        } else {
            delete next.curriculum_detail
        }
    }

    if (next.curriculum?.length) {
        next.curriculum = next.curriculum.map((line) => ({
            ...line,
            curriculum_detail: sanitizeCurriculumDetailPayload(line.curriculum_detail),
        }))
        delete next.curriculum_id
        delete next.organisation_curriculum_id
        delete next.curriculum_detail
    }

    return next
}

function curriculumLineWireKey(line: IStageCurriculumLine): string {
    const grades = [...(line.curriculum_detail?.grades ?? [])].sort((a, b) =>
        String(a.grade).localeCompare(String(b.grade), undefined, { numeric: true }),
    )
    return JSON.stringify({
        organisation_curriculum_id: line.organisation_curriculum_id,
        curriculum_id: line.curriculum_id,
        grades,
    })
}

export function buildS1CurriculumLineFromCard(
    card: TS1BoardCardForCurriculumApi,
    getCurriculumIdByBoardName: (boardName: string) => number | null | undefined,
): IStageCurriculumLine | null {
    const boardName = String(card.board || '').trim()
    const curriculumIdRaw =
        card.curriculum_id ?? (boardName ? getCurriculumIdByBoardName(boardName) : null)
    const curriculumId = curriculumIdRaw != null ? Number(curriculumIdRaw) : NaN
    if (!Number.isFinite(curriculumId) || curriculumId <= 0) {
        return null
    }

    const grades: IStageCurriculumGradeRow[] = (card.gradeRows || [])
        .map((row) => {
            const grade = row.grade
            const studentStrength = Number(
                String(row.studentStrength ?? '')
                    .replace(/,/g, '')
                    .trim() || 0,
            )
            if (grade === undefined || grade === '') return null
            return {
                grade,
                student_strength: Number.isFinite(studentStrength) ? studentStrength : 0,
            }
        })
        .filter((row): row is IStageCurriculumGradeRow => row !== null)

    const line: IStageCurriculumLine = {
        curriculum_id: curriculumId,
        curriculum_detail: grades.length ? { grades } : {},
    }
    const orgCurId = card.organisation_curriculum_id
    if (orgCurId != null && Number(orgCurId) > 0) {
        line.organisation_curriculum_id = Number(orgCurId)
    }
    return line
}

/**
 * Stage PATCH: only new cards (no organisation_curriculum_id) or lines that differ from snapshot.
 * Deletes are sent separately as curriculum_delete_ids.
 */
export function diffS1CurriculumLinesForStageApi(
    previousCards: TS1BoardCardForCurriculumApi[],
    currentCards: TS1BoardCardForCurriculumApi[],
    getCurriculumIdByBoardName: (boardName: string) => number | null | undefined,
): IStageCurriculumLine[] {
    const prevByOrgId = new Map<number, IStageCurriculumLine>()
    for (const card of previousCards) {
        const orgId = card.organisation_curriculum_id
        if (orgId == null || Number(orgId) <= 0) continue
        const line = buildS1CurriculumLineFromCard(card, getCurriculumIdByBoardName)
        if (line) {
            prevByOrgId.set(Number(orgId), line)
        }
    }

    const out: IStageCurriculumLine[] = []
    for (const card of currentCards) {
        const line = buildS1CurriculumLineFromCard(card, getCurriculumIdByBoardName)
        if (!line) continue

        const orgId = card.organisation_curriculum_id
        if (orgId == null || Number(orgId) <= 0) {
            out.push(line)
            continue
        }

        const prevLine = prevByOrgId.get(Number(orgId))
        if (!prevLine) {
            out.push(line)
            continue
        }

        if (curriculumLineWireKey(line) !== curriculumLineWireKey(prevLine)) {
            out.push(line)
        }
    }
    return out
}

export function buildS1CurriculumLinesForStageApi(
    boardCards: TS1BoardCardForCurriculumApi[],
    getCurriculumIdByBoardName: (boardName: string) => number | null | undefined,
): IStageCurriculumLine[] {
    const lines: IStageCurriculumLine[] = []
    for (const card of boardCards) {
        const line = buildS1CurriculumLineFromCard(card, getCurriculumIdByBoardName)
        if (line) {
            lines.push(line)
        }
    }
    return lines
}
